/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { guacHttpClient } from "@/lib/http";
import {
  CompanyFileWithSignedUrlResponse,
  HttpResponse,
  MediaLink,
  MediaLinkType,
} from "@/types";

export const media = {
  extractYouTubeVideoId: (url: string): string | null => {
    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.replace("www.", "");
      if (hostname === "youtu.be") {
        const id = parsed.pathname.slice(1).trim();
        return id || null;
      }
      if (hostname === "youtube.com") {
        // Handle /embed/VIDEO_ID and /shorts/VIDEO_ID paths.
        const segments = parsed.pathname.split("/").filter(Boolean);
        if (
          (segments[0] === "embed" || segments[0] === "shorts") &&
          segments[1]
        ) {
          const id = segments[1].trim();
          return id || null;
        }
        // Standard ?v= watch URLs.
        const v = parsed.searchParams.get("v")?.trim();
        return v || null;
      }
      return null;
    } catch {
      return null;
    }
  },

  isYouTubeUrl: (url: string): boolean => {
    return media.extractYouTubeVideoId(url) !== null;
  },

  createYouTubeThumbnailLink: (url: string): string | null => {
    const videoId = media.extractYouTubeVideoId(url);
    if (!videoId) return null;
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  },

  /**
   * Fetches signed URLs for all uploaded-image media links and returns
   * the full array with `url` replaced by the signed URL where applicable.
   */
  resolveMediaLinkSignedUrls: async (
    mediaLinks: MediaLink[],
    companyId: string,
  ): Promise<MediaLink[]> => {
    const imageLinks = mediaLinks.filter(
      (m) => m.type === MediaLinkType.UPLOADED_IMAGE && m.companyFileId != null,
    );

    if (imageLinks.length === 0) return mediaLinks;

    const results = await Promise.all(
      imageLinks.map(async (ml) => {
        const path = `companies/${companyId}/files/${String(ml.companyFileId)}`;
        const res = await guacHttpClient.get<
          CompanyFileWithSignedUrlResponse,
          HttpResponse<CompanyFileWithSignedUrlResponse>
        >(path);
        return { id: ml.id, signedUrl: res.data.signedUrl };
      }),
    );

    const signedUrlById: Record<string, string> = {};
    for (const r of results) {
      signedUrlById[r.id] = r.signedUrl;
    }

    return mediaLinks.map((m) => {
      const signedUrl = signedUrlById[m.id];
      return signedUrl ? { ...m, url: signedUrl } : m;
    });
  },
};
